import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast, Toaster } from "sonner";
import ImageUploadInput from "../../components/ImageUploadInput";

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // category being edited
    const [form, setForm] = useState({ name: "", description: "", image_url: "", active: true });

    const load = () => api.get("/categories", { params: { active_only: false } }).then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const startNew = () => { setEditing("new"); setForm({ name: "", description: "", image_url: "", active: true }); };
    const startEdit = (c) => { setEditing(c.id); setForm({ name: c.name, description: c.description || "", image_url: c.image_url || "", active: c.active }); };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Name required");
        try {
            if (editing === "new") await api.post("/categories", form);
            else await api.put(`/categories/${editing}`, form);
            toast.success("Saved"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this category?")) return;
        await api.delete(`/categories/${id}`); toast.success("Deleted"); load();
    };

    return (
        <div data-testid="admin-categories">
            <Toaster position="top-center" />
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="label-eyebrow text-sky-700">Editable tabs</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Medicine categories</h1>
                </div>
                <Button onClick={startNew} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-category"><Plus className="w-4 h-4 mr-2" />Add category</Button>
            </div>

            {editing && (
                <div className="bg-white border border-stone-200 p-5 mb-6">
                    <h2 className="font-serif text-2xl mb-4">{editing === "new" ? "New" : "Edit"} category</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="rounded-none border-stone-300" data-testid="cat-name" />
                        <div /> {/* spacer so name sits alone on the row */}
                    </div>
                    <div className="mt-3">
                        <ImageUploadInput
                            label="Category image (optional)"
                            value={form.image_url}
                            onChange={(v) => setForm({ ...form, image_url: v })}
                            testId="cat-img"
                            multiple={false}
                            hint="Square works best. Falls back to a placeholder if empty."
                        />
                    </div>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="rounded-none border-stone-300 mt-3" data-testid="cat-desc" />
                    <label className="flex items-center justify-between border border-stone-300 p-3 mt-3"><span className="text-sm">Active (visible to public)</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cat-active" /></label>
                    <div className="flex gap-2 mt-4">
                        <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="cat-save">Save</Button>
                        <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-stone-200">
                {items.map((c) => (
                    <div key={c.id} className="border-b border-stone-100 last:border-b-0 p-4 flex items-center gap-4" data-testid={`cat-row-${c.slug}`}>
                        <div className="flex-1">
                            <div className="font-medium">{c.name} {!c.active && <span className="text-xs uppercase text-stone-400 ml-2">hidden</span>}</div>
                            <div className="text-xs text-stone-500 line-clamp-1">{c.description}</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => startEdit(c)} className="rounded-none" data-testid={`cat-edit-${c.slug}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => del(c.id)} className="rounded-none text-red-700" data-testid={`cat-del-${c.slug}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                ))}
                {items.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No categories yet.</div>}
            </div>
        </div>
    );
}
